import React, { useCallback, useEffect, useState } from 'react';
import useApiStable from '../../utils/useApiStable';
import '../../styles/KnockoutPhase.css';
import MatchResultInput from '../MatchResultInput';
import { isUserAdmin, canExportKnockoutBracket } from '../../utils/userUtils';
import { downloadFileFromApi } from '../../utils/downloadFile';

const normalizeArray = (data) => (Array.isArray(data) ? data : []);

const roundOrder = ['FINAL', 'THIRD_PLACE', 'SEMI_FINAL', 'QUARTER_FINAL', 'ROUND_16']; // Finale oben

const roundLabels = {
  ROUND_16: 'Achtelfinale',
  QUARTER_FINAL: 'Viertelfinale',
  SEMI_FINAL: 'Halbfinale',
  THIRD_PLACE: 'Spiel um Platz 3',
  FINAL: 'Finale',
};

/**
 * Gemeinsame K.O.-Baum-Darstellung inkl. Team-Tausch (Drag & Drop / Klick), Ergebniseingabe,
 * Terminierung und provisorischem Match anlegen. Wird sowohl von der eigenständigen Seite
 * `/knockout-phase` (KnockoutPhase.js) als auch von der in die Turnierverwaltung eingebetteten
 * Ansicht (Tournaments.js) verwendet, damit beide Stellen dieselbe Funktionalität haben und nicht
 * auseinanderdriften.
 */
const KnockoutBracket = ({
  tournamentId,
  tournamentName,
  user,
  onBackToGroupPhase,
  initialMatchId,
  onInitialMatchConsumed,
}) => {
  const api = useApiStable();
  const [knockoutMatches, setKnockoutMatches] = useState([]);
  const [players, setPlayers] = useState([]);
  const [editingMatch, setEditingMatch] = useState(null);
  const [schedulingMatch, setSchedulingMatch] = useState(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduleCourtId, setScheduleCourtId] = useState('');
  const [availableCourts, setAvailableCourts] = useState([]);
  const [creatingProvisionalMatch, setCreatingProvisionalMatch] = useState(false);
  const [provisionalMatchData, setProvisionalMatchData] = useState({ round: '', matchNumber: '' });
  const [dragSource, setDragSource] = useState(null); // { matchId, slot }
  const [dropTargetKey, setDropTargetKey] = useState(null); // `${matchId}:${slot}`
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // `silent` unterdrückt den Ladezustand: wird nach einer Aktion (Terminierung, Ergebnis, Tausch, ...)
  // nachgeladen, würde `loading=true` den kompletten Baum kurz durch den Ladetext ersetzen und danach
  // neu aufbauen - dabei schrumpft die Seite kurzzeitig auf eine Zeile, der Browser verliert die
  // Scroll-Position und springt nach oben (wirkt wie ein Seiten-Reload). Beim allerersten Laden bzw.
  // Turnierwechsel bleibt der Ladezustand sichtbar, da dort noch keine sinnvolle Ansicht existiert.
  const loadKnockoutData = useCallback(async ({ silent = false } = {}) => {
    if (!tournamentId) {
      setKnockoutMatches([]);
      setPlayers([]);
      setError(null);
      return;
    }

    if (!silent) {
      setLoading(true);
    }
    try {
      const [matchesData, playersData] = await Promise.all([
        api.get(`/tournaments/knockout/${tournamentId}/matches`),
        api.get(`/tournaments/${tournamentId}/teams`),
      ]);
      setKnockoutMatches(normalizeArray(matchesData));
      setPlayers(normalizeArray(playersData));
      setError(null);
    } catch (loadError) {
      console.error('Fehler beim Laden der K.O.-Daten:', loadError);
      setKnockoutMatches([]);
      setPlayers([]);
      setError(loadError.message || 'Die K.O.-Phase konnte nicht geladen werden.');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [api, tournamentId]);

  useEffect(() => {
    loadKnockoutData();
  }, [loadKnockoutData]);

  // Plätze der zum Turnier gehörenden CourtGroup laden, für das Terminierungs-Dropdown. Vorher war
  // "Platz" ein Freitextfeld, in dem z. B. eine eingetippte "1" beim Speichern über die Court-ID
  // stillschweigend zu "Platz 1" aufgelöst wurde - verwirrend, da nicht ersichtlich war, dass es sich
  // um eine ID-Zuordnung statt eines Freitexts handelte.
  useEffect(() => {
    let cancelled = false;
    if (!tournamentId) {
      setAvailableCourts([]);
      return undefined;
    }

    (async () => {
      try {
        const [tournaments, courtGroups] = await Promise.all([
          api.get('/tournaments'),
          api.get('/courts/courtGroups'),
        ]);
        const tournament = normalizeArray(tournaments).find((t) => t.id === tournamentId);
        const courtGroup = normalizeArray(courtGroups).find((group) => String(group.id) === String(tournament?.courtGroupId));
        const sortedCourts = normalizeArray(courtGroup?.courts)
          .slice()
          .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de', { numeric: true, sensitivity: 'base' }));
        if (!cancelled) {
          setAvailableCourts(sortedCourts);
        }
      } catch (loadError) {
        console.error('Fehler beim Laden der Plätze:', loadError);
        if (!cancelled) setAvailableCourts([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [api, tournamentId]);

  useEffect(() => {
    setEditingMatch(null);
    setSchedulingMatch(null);
    setCreatingProvisionalMatch(false);
    setProvisionalMatchData({ round: '', matchNumber: '' });
    setDragSource(null);
    setDropTargetKey(null);
  }, [tournamentId]);

  // Deep-Link aus der Startseite: K.O.-Match öffnen, sobald es geladen ist.
  useEffect(() => {
    if (initialMatchId == null) return;
    const match = knockoutMatches.find((candidate) => candidate.id === initialMatchId);
    if (match) {
      setEditingMatch(match);
      onInitialMatchConsumed?.();
    }
  }, [initialMatchId, knockoutMatches, onInitialMatchConsumed]);

  const formatMatchDate = (match) => {
    const rawValue = match?.startTime || match?.date;
    if (!rawValue) return null;

    const parsedDate = new Date(rawValue);
    if (Number.isNaN(parsedDate.getTime())) {
      return String(rawValue);
    }

    return parsedDate.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getParticipantName = (participant) => {
    if (!participant) return 'TBD';
    if (participant.displayName) return participant.displayName;
    if (participant.name) return participant.name;
    if (Array.isArray(participant.players) && participant.players.length > 0) {
      return participant.players.map((player) => player.name).join(' / ');
    }
    return `Team ${participant.id}`;
  };

  const getTeamName = (team) => {
    if (!team) return 'TBD';
    const hydratedTeam = players.find((entry) => entry.id === team.id) || team;
    return getParticipantName(hydratedTeam);
  };

  const teamContainsCurrentUser = (team) => {
    if (!team || !user) return false;

    if (Array.isArray(team.players)) {
      return team.players.some((player) => {
        if (!player) return false;
        if (player.personId != null && user.personId != null) {
          return Number(player.personId) === Number(user.personId);
        }
        if (player.name && user.name) {
          return player.name.trim().toLowerCase() === user.name.trim().toLowerCase();
        }
        return false;
      });
    }

    if (team.personId != null && user.personId != null) {
      return Number(team.personId) === Number(user.personId);
    }

    return Boolean(team.name && user.name && team.name.trim().toLowerCase() === user.name.trim().toLowerCase());
  };

  const canManageMatch = (match) => {
    if (!user || !match) return false;
    return isUserAdmin(user) || teamContainsCurrentUser(match.team1) || teamContainsCurrentUser(match.team2);
  };

  // Match-Ergebnis mit Sätzen eintragen
  const handleMatchSets = async (matchId, sets) => {
    try {
      await api.put(`/tournaments/matches/${matchId}/sets`, sets);
      await loadKnockoutData({ silent: true });
      setEditingMatch(null);
    } catch (saveError) {
      alert('Fehler beim Speichern der Satz-Ergebnisse: ' + saveError.message);
    }
  };

  const handleDeleteMatchResult = async (match) => {
    if (!match?.id || !isUserAdmin(user)) return;

    const confirmed = window.confirm(
      'Soll das Ergebnis dieses K.O.-Spiels wirklich gelöscht werden? Abhängige Folgebegegnungen werden dabei ebenfalls zurückgesetzt.'
    );
    if (!confirmed) return;

    try {
      await api.delete(`/tournaments/matches/${match.id}/sets`);
      await loadKnockoutData({ silent: true });
      if (editingMatch?.id === match.id) {
        setEditingMatch(null);
      }
    } catch (deleteError) {
      alert('Fehler beim Löschen des Ergebnisses: ' + deleteError.message);
    }
  };

  const exportBracketAsPdf = async () => {
    if (!tournamentId) return;
    try {
      await downloadFileFromApi(
        api,
        `/tournaments/knockout/${tournamentId}/export/bracket.pdf`,
        `ko-baum-${tournamentId}.pdf`
      );
    } catch (exportError) {
      alert(exportError.message || 'PDF-Export ist fehlgeschlagen.');
    }
  };

  const toDateInputValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const toTimeInputValue = (date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Vorbelegung von Datum/Uhrzeit/Platz beim Öffnen des Terminierungs-Dialogs
  const openScheduleModal = (match) => {
    const rawValue = match?.startTime || match?.date;
    const parsedDate = rawValue ? new Date(rawValue) : null;
    const hasValidDate = parsedDate && !Number.isNaN(parsedDate.getTime());

    setSchedulingMatch(match);
    setScheduleDate(hasValidDate ? toDateInputValue(parsedDate) : '');
    setScheduleTime(hasValidDate ? toTimeInputValue(parsedDate) : '');
    setScheduleCourtId(match.courtId != null ? String(match.courtId) : '');
  };

  // Terminierung speichern
  const handleScheduleMatch = async () => {
    if (!schedulingMatch) return;
    if (!scheduleDate) {
      alert('Bitte ein Datum auswählen');
      return;
    }
    try {
      const time = scheduleTime || '00:00';
      const payload = { startTime: `${scheduleDate} ${time}:00` };
      if (scheduleCourtId) {
        payload.courtId = Number(scheduleCourtId);
      }
      await api.put(`/tournaments/knockout/matches/${schedulingMatch.id}/schedule`, payload);
      await loadKnockoutData({ silent: true });
      setSchedulingMatch(null);
      setScheduleDate('');
      setScheduleTime('');
      setScheduleCourtId('');
    } catch (saveError) {
      alert('Fehler beim Speichern der Terminierung: ' + saveError.message);
    }
  };

  // Terminierung (Datum, Uhrzeit, Platz) wieder zurücksetzen
  const handleResetSchedule = async () => {
    if (!schedulingMatch) return;
    if (!window.confirm('Soll die Terminierung dieses Matches wirklich entfernt werden?')) return;
    try {
      await api.put(`/tournaments/knockout/matches/${schedulingMatch.id}/schedule`, {
        startTime: null,
        courtId: null,
      });
      await loadKnockoutData({ silent: true });
      setSchedulingMatch(null);
      setScheduleDate('');
      setScheduleTime('');
      setScheduleCourtId('');
    } catch (resetError) {
      alert('Fehler beim Zurücksetzen der Terminierung: ' + resetError.message);
    }
  };

  // Provisionales Match erstellen
  const handleCreateProvisionalMatch = async () => {
    if (!provisionalMatchData.round || !provisionalMatchData.matchNumber) {
      alert('Bitte Runde und Match-Nummer auswählen');
      return;
    }

    try {
      await api.post('/tournaments/knockout/matches', {
        tournamentId,
        round: provisionalMatchData.round,
        matchNumber: parseInt(provisionalMatchData.matchNumber, 10),
      });
      await loadKnockoutData({ silent: true });
      setCreatingProvisionalMatch(false);
      setProvisionalMatchData({ round: '', matchNumber: '' });
    } catch (saveError) {
      alert('Fehler beim Erstellen des provisorischen Matches: ' + saveError.message);
    }
  };

  // Matches nach Runden gruppieren
  const groupMatchesByRound = () => {
    const rounds = {};
    knockoutMatches.forEach((match) => {
      if (!rounds[match.round]) {
        rounds[match.round] = [];
      }
      rounds[match.round].push(match);
    });
    return rounds;
  };

  const roundsData = groupMatchesByRound();

  // Solange kein Match einer Runde ein Ergebnis hat, dürfen Gegner per Drag & Drop / Klick getauscht werden
  const isRoundLocked = (round) => (roundsData[round] || []).some((match) => match.completed || match.winnerTeam);

  const handleDragStart = (match, slot) => (event) => {
    setDragSource({ matchId: match.id, slot });
    event.dataTransfer.effectAllowed = 'move';
    // Ohne gesetzte Nutzdaten starten manche Browser (v.a. Firefox) den nativen Drag gar nicht erst
    // - dragover/drop würden dann nie feuern. Der eigentliche Inhalt wird über den dragSource-State
    // transportiert, hier reicht ein Platzhalterwert.
    event.dataTransfer.setData('text/plain', `${match.id}:${slot}`);
  };

  const handleDragEnd = () => {
    setDragSource(null);
    setDropTargetKey(null);
  };

  const handleDragOverSlot = (match, slot) => (event) => {
    if (!dragSource || isRoundLocked(match.round)) return;
    event.preventDefault();
    const key = `${match.id}:${slot}`;
    if (dropTargetKey !== key) setDropTargetKey(key);
  };

  const handleDragLeaveSlot = () => setDropTargetKey(null);

  const performSwap = async (source, target) => {
    if (source.matchId === target.matchId && source.slot === target.slot) return;

    try {
      await api.put('/tournaments/knockout/matches/swap-teams', {
        matchId1: source.matchId,
        slot1: source.slot,
        matchId2: target.matchId,
        slot2: target.slot,
      });
      await loadKnockoutData({ silent: true });
    } catch (swapError) {
      alert('Tausch nicht möglich: ' + swapError.message);
    }
  };

  const handleDropOnSlot = (match, slot) => async (event) => {
    event.preventDefault();
    setDropTargetKey(null);
    if (!dragSource) return;
    const target = { matchId: match.id, slot };
    const source = dragSource;
    setDragSource(null);
    await performSwap(source, target);
  };

  // Klick-basierte Alternative zum nativen Drag & Drop: erster Klick wählt einen Platz aus (wie beim
  // Greifen per Maus), ein zweiter Klick auf einen anderen Platz löst den Tausch aus. Erlaubt den
  // Tausch auch auf Touch-Geräten (Tablet/Handy), auf denen natives HTML5-Drag&Drop nicht
  // funktioniert, und ist unabhängig von Browser-Eigenheiten beim nativen Drag.
  const handlePlayerClick = (match, slot) => () => {
    const target = { matchId: match.id, slot };

    if (!dragSource) {
      setDragSource(target);
      return;
    }

    if (dragSource.matchId === target.matchId && dragSource.slot === target.slot) {
      setDragSource(null);
      return;
    }

    const source = dragSource;
    setDragSource(null);
    performSwap(source, target);
  };

  const renderKnockoutParticipant = (match, slot, isWinner, isSwappable, dropKey) => {
    const team = slot === 'TEAM1' ? match.team1 : match.team2;
    const sourceLabel = slot === 'TEAM1' ? match.team1Source : match.team2Source;
    const canSwap = isUserAdmin(user) && !isRoundLocked(match.round);
    return (
      <div
        className={`knockout-player ${isWinner && team ? 'winner' : ''} ${isSwappable ? 'knockout-player-draggable' : ''} ${dropTargetKey === dropKey ? 'knockout-player-drop-target' : ''} ${dragSource?.matchId === match.id && dragSource?.slot === slot ? 'knockout-player-drag-source' : ''}`}
        draggable={isSwappable}
        onDragStart={isSwappable ? handleDragStart(match, slot) : undefined}
        onDragEnd={isSwappable ? handleDragEnd : undefined}
        onDragOver={canSwap ? handleDragOverSlot(match, slot) : undefined}
        onDragLeave={canSwap ? handleDragLeaveSlot : undefined}
        onDrop={canSwap ? handleDropOnSlot(match, slot) : undefined}
        onClick={isSwappable ? handlePlayerClick(match, slot) : undefined}
        title={isSwappable ? 'Klicken oder ziehen, um Gegner innerhalb der Runde zu tauschen' : undefined}
      >
        <span className="knockout-player-info">
          <span className="knockout-player-name">{getTeamName(team)}</span>
          {sourceLabel && <span className="knockout-player-origin">{sourceLabel}</span>}
        </span>
        {isWinner && team && getTeamName(team) !== 'TBD' && <span className="knockout-winner-icon">👑</span>}
      </div>
    );
  };

  return (
    <div className="groupphase-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ marginBottom: 4 }}>K.O.-Phase</h2>
          {tournamentName && (
            <p className="groupphase-help-text" style={{ margin: 0 }}>
              Aktuelle Übersicht für <strong>{tournamentName}</strong>.
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {canExportKnockoutBracket(user) && knockoutMatches.length > 0 && (
            <button onClick={exportBracketAsPdf} className="group-button">
              K.O.-Baum als PDF
            </button>
          )}
          {onBackToGroupPhase && (
            <button onClick={onBackToGroupPhase} className="group-button">
              Zurück zur Gruppenphase
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <p className="groupphase-help-text">Lade K.O.-Phase ...</p>
      ) : error ? (
        <p className="groupphase-error-text">{error}</p>
      ) : knockoutMatches.length === 0 ? (
        <div className="knockout-empty">
          <p>Noch keine K.O.-Matches verfügbar.</p>
          {isUserAdmin(user) && (
            <button onClick={() => setCreatingProvisionalMatch(true)} className="knockout-create-button">
              Provisorisches Match erstellen
            </button>
          )}
        </div>
      ) : (
        <div className="knockout-container" style={{ padding: '20px 0 0' }}>
          <div className="knockout-bracket">
            {roundOrder.map((round) => {
              if (!roundsData[round]) return null;

              return (
                <div key={round} className="knockout-round">
                  <h3 className="knockout-round-title">{roundLabels[round] || round}</h3>
                  <div className="knockout-matches">
                    {roundsData[round].map((match) => {
                      const team1Swappable = isUserAdmin(user) && !isRoundLocked(match.round) && (Boolean(match.team1) || Boolean(match.team1Source));
                      const team2Swappable = isUserAdmin(user) && !isRoundLocked(match.round) && (Boolean(match.team2) || Boolean(match.team2Source));
                      return (
                        <div key={match.id} className="knockout-match">
                          <div className="knockout-match-header">
                            <span className="knockout-match-number">Match {match.matchNumber}</span>
                            {formatMatchDate(match) && <span className="knockout-match-date">{formatMatchDate(match)}</span>}
                            {/* match.court enthält bereits den vollen Platznamen (z.B. "Platz 1") */}
                            {match.court && <span className="knockout-match-court">{match.court}</span>}
                          </div>

                          <div className="knockout-players">
                            {renderKnockoutParticipant(
                              match,
                              'TEAM1',
                              match.winnerTeam?.id === match.team1?.id,
                              team1Swappable,
                              `${match.id}:TEAM1`
                            )}

                            <div className="knockout-vs">vs</div>

                            {renderKnockoutParticipant(
                              match,
                              'TEAM2',
                              match.winnerTeam?.id === match.team2?.id,
                              team2Swappable,
                              `${match.id}:TEAM2`
                            )}
                          </div>

                          <div className="knockout-match-actions">
                            {canManageMatch(match) && (
                              <button onClick={() => openScheduleModal(match)} className="knockout-schedule-button">
                                Terminieren
                              </button>
                            )}
                            {match.completed ? (
                              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                                <span className="knockout-completed">✓ Abgeschlossen</span>
                                {isUserAdmin(user) && (
                                  <>
                                    <button onClick={() => setEditingMatch(match)} className="knockout-enter-result-button">
                                      Ergebnis ändern
                                    </button>
                                    <button
                                      onClick={() => handleDeleteMatchResult(match)}
                                      className="knockout-back-button"
                                      style={{ padding: '10px 20px' }}
                                    >
                                      Ergebnis löschen
                                    </button>
                                  </>
                                )}
                              </div>
                            ) : match.team1 && match.team2 ? (
                              canManageMatch(match) && (
                                <button onClick={() => setEditingMatch(match)} className="knockout-enter-result-button">
                                  Ergebnis eintragen
                                </button>
                              )
                            ) : (
                              // Der "Terminieren"-Button oben deckt auch diesen Fall (Teilnehmer noch nicht
                              // feststehend) bereits ab - hier daher kein zweiter, redundanter Button mehr.
                              <span className="knockout-waiting">Warten auf vorherige Runde</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Overlay für Ergebnis-Eingabe */}
      {editingMatch && (
        <div className="knockout-overlay">
          <div className="knockout-overlay-content">
            <button className="knockout-overlay-close" onClick={() => setEditingMatch(null)} title="Schließen">
              &times;
            </button>

            <h2>Ergebnis eintragen</h2>
            <div style={{ marginBottom: 16 }}>
              <b>{getTeamName(editingMatch.team1)}</b>
              &nbsp;vs.&nbsp;
              <b>{getTeamName(editingMatch.team2)}</b>
            </div>
            <MatchResultInput match={editingMatch} onSave={handleMatchSets} />
          </div>
        </div>
      )}

      {/* Modal für Terminierung */}
      {schedulingMatch && (
        <div className="knockout-modal">
          <div className="knockout-modal-content">
            <h3>Match terminieren</h3>
            <label>
              Datum:
              <input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} />
            </label>
            <label>
              Uhrzeit (optional):
              <input type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} />
            </label>
            <label>
              Platz:
              <select value={scheduleCourtId} onChange={(e) => setScheduleCourtId(e.target.value)}>
                <option value="">Kein Platz zugewiesen</option>
                {availableCourts.map((court) => (
                  <option key={court.id} value={court.id}>
                    {court.name}
                  </option>
                ))}
              </select>
            </label>
            <div className="knockout-modal-actions">
              <button onClick={handleScheduleMatch} className="knockout-save-button">Speichern</button>
              {(schedulingMatch?.startTime || schedulingMatch?.courtId != null) && (
                <button onClick={handleResetSchedule} className="knockout-reset-button">Terminierung entfernen</button>
              )}
              <button onClick={() => setSchedulingMatch(null)} className="knockout-cancel-button">Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal für provisionales Match erstellen */}
      {creatingProvisionalMatch && (
        <div className="knockout-modal">
          <div className="knockout-modal-content">
            <h3>Provisorisches K.O.-Match erstellen</h3>
            <label>
              Runde:
              <select
                value={provisionalMatchData.round}
                onChange={(e) => setProvisionalMatchData({ ...provisionalMatchData, round: e.target.value })}
              >
                <option value="">Runde auswählen</option>
                <option value="ROUND_16">Achtelfinale</option>
                <option value="QUARTER_FINAL">Viertelfinale</option>
                <option value="SEMI_FINAL">Halbfinale</option>
                <option value="THIRD_PLACE">Spiel um Platz 3</option>
                <option value="FINAL">Finale</option>
              </select>
            </label>
            <label>
              Match-Nummer:
              <input
                type="number"
                min="1"
                value={provisionalMatchData.matchNumber}
                onChange={(e) => setProvisionalMatchData({ ...provisionalMatchData, matchNumber: e.target.value })}
              />
            </label>
            <div className="knockout-modal-actions">
              <button onClick={handleCreateProvisionalMatch} className="knockout-save-button">Erstellen</button>
              <button onClick={() => setCreatingProvisionalMatch(false)} className="knockout-cancel-button">Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnockoutBracket;
